import { Ionicons } from '@expo/vector-icons';
import * as Device from 'expo-device';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  I18nManager,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import notificationService, { NotificationPreferences } from '../services/notificationService';
import { api } from '../services/api';
import logger from '../utils/logger';

interface NotificationSettingsScreenProps {
  navigation: any;
}

export default function NotificationSettingsScreen({
  navigation,
}: NotificationSettingsScreenProps) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [syncWarning, setSyncWarning] = useState(false);
  const [showTimePickerStart, setShowTimePickerStart] = useState(false);
  const [showTimePickerEnd, setShowTimePickerEnd] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    alarmsEnabled: true,
    lowBatteryEnabled: true,
    deviceOfflineEnabled: true,
    soundEnabled: true,
    vibrationEnabled: true,
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
  });

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const isPhysicalDevice = Device.isDevice;

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إعدادات الإشعارات' : 'Notification Settings',
      headerTintColor: '#ffffff',
      headerTitleStyle: {
        color: '#ffffff',
        fontWeight: '700',
      },
    });
    loadPreferences();
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    if (!isPhysicalDevice || Platform.OS === 'web') {
      setPermissionsGranted(false);
      return;
    }

    try {
      const result = await notificationService.ensureNotificationsPermission();
      setPermissionsGranted(result.granted);
      logger.log('🔔 Permissions check result:', result);
    } catch (error) {
      logger.error('Failed to check permissions:', error);
      setPermissionsGranted(false);
    }
  };

  const loadPreferences = async () => {
    try {
      setLoading(true);
      // Load local preferences first (instant)
      const localPrefs = await notificationService.getPreferences();
      setPreferences(localPrefs);

      // Fetch server-side preferences and merge (best-effort)
      try {
        const { data: serverPrefs } = await api.get('/notifications/preferences');
        if (serverPrefs) {
          const merged: NotificationPreferences = {
            ...localPrefs,
            // Map backend fields to mobile field names
            alarmsEnabled: serverPrefs.pushEnabled ?? localPrefs.alarmsEnabled,
            deviceOfflineEnabled:
              serverPrefs.deviceOfflineEnabled ?? localPrefs.deviceOfflineEnabled,
            lowBatteryEnabled: serverPrefs.lowBatteryEnabled ?? localPrefs.lowBatteryEnabled,
            quietHoursEnabled: serverPrefs.quietHours?.enabled ?? localPrefs.quietHoursEnabled,
            quietHoursStart: serverPrefs.quietHours?.start ?? localPrefs.quietHoursStart,
            quietHoursEnd: serverPrefs.quietHours?.end ?? localPrefs.quietHoursEnd,
          };
          setPreferences(merged);
          // Persist merged preferences locally
          await notificationService.savePreferences(merged);
        }
      } catch {
        // Backend unreachable — local prefs are fine
      }
    } catch (error) {
      logger.error('Failed to load notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key: keyof NotificationPreferences, value: any) => {
    // Check permissions before allowing toggle changes (except for sound/vibration which are local)
    const localOnlySettings = [
      'soundEnabled',
      'vibrationEnabled',
      'quietHoursEnabled',
      'quietHoursStart',
      'quietHoursEnd',
    ];

    if (!localOnlySettings.includes(key)) {
      const permissionResult = await notificationService.ensureNotificationsPermission();
      if (!permissionResult.granted) {
        handlePermissionDenied(permissionResult);
        return;
      }
    }

    try {
      const updated = { ...preferences, [key]: value };
      setPreferences(updated);
      // ✅ Save the ENTIRE preferences object
      await notificationService.savePreferences(updated);
      // BUG-007: Sync preferences to backend (best-effort, show warning on failure)
      try {
        await api.patch('/notifications/settings', updated);
        setSyncWarning(false);
      } catch {
        setSyncWarning(true);
        setTimeout(() => setSyncWarning(false), 4000);
      }
      logger.log(`✅ Preference ${key} updated to:`, value);
    } catch (error) {
      logger.error('Failed to update preference:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل حفظ الإعدادات' : 'Failed to save settings',
      );
    }
  };

  const updateQuietHours = async (type: 'start' | 'end', time: string) => {
    const key = type === 'start' ? 'quietHoursStart' : 'quietHoursEnd';
    await updatePreference(key, time);
  };

  // Time Picker Component
  const renderTimePicker = (type: 'start' | 'end') => {
    const isVisible = type === 'start' ? showTimePickerStart : showTimePickerEnd;
    const setVisible = type === 'start' ? setShowTimePickerStart : setShowTimePickerEnd;
    const currentTime = type === 'start' ? preferences.quietHoursStart : preferences.quietHoursEnd;
    const [hour, minute] = currentTime.split(':').map(Number);

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    return (
      <Modal
        visible={isVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setVisible(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setVisible(false)}>
          <Pressable
            style={[styles.timePickerContainer, { backgroundColor: theme.colors.card }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={[styles.timePickerHeader, { borderBottomColor: theme.colors.border }]}>
              <Text style={[styles.timePickerTitle, { color: theme.colors.text }]}>
                {type === 'start'
                  ? isArabic
                    ? 'وقت البداية'
                    : 'Start Time'
                  : isArabic
                  ? 'وقت النهاية'
                  : 'End Time'}
              </Text>
            </View>

            <View style={[styles.timePickerContent, { flexDirection: 'row' }]}>
              {/* Hours column */}
              <View style={{ flex: 1 }}>
                <Text style={[styles.timePickerColumnLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'ساعة' : 'Hour'}
                </Text>
                <ScrollView style={styles.timePicker} showsVerticalScrollIndicator={false}>
                  {hours.map((h) => (
                    <TouchableOpacity
                      key={`hour-${h}`}
                      style={[
                        styles.timeOption,
                        {
                          backgroundColor: h === hour ? theme.colors.primary + '20' : 'transparent',
                        },
                      ]}
                      onPress={() => {
                        const newTime = `${h.toString().padStart(2, '0')}:${minute
                          .toString()
                          .padStart(2, '0')}`;
                        updateQuietHours(type, newTime);
                      }}
                      accessible={true}
                      accessibilityLabel={`${h.toString().padStart(2, '0')}${
                        h === hour ? ` (${isArabic ? 'محدد' : 'selected'})` : ''
                      }`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: h === hour }}
                    >
                      <Text
                        style={[
                          styles.timeOptionText,
                          {
                            color: h === hour ? theme.colors.primary : theme.colors.text,
                            fontWeight: h === hour ? '700' : '400',
                          },
                        ]}
                      >
                        {h.toString().padStart(2, '0')}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Separator */}
              <Text style={[styles.timePickerSeparator, { color: theme.colors.text }]}>:</Text>

              {/* Minutes column (5-minute intervals) */}
              <View style={{ flex: 1 }}>
                <Text style={[styles.timePickerColumnLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'دقيقة' : 'Min'}
                </Text>
                <ScrollView style={styles.timePicker} showsVerticalScrollIndicator={false}>
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <TouchableOpacity
                      key={`min-${m}`}
                      style={[
                        styles.timeOption,
                        {
                          backgroundColor:
                            m === minute ? theme.colors.primary + '20' : 'transparent',
                        },
                      ]}
                      onPress={() => {
                        const newTime = `${hour.toString().padStart(2, '0')}:${m
                          .toString()
                          .padStart(2, '0')}`;
                        updateQuietHours(type, newTime);
                      }}
                      accessible={true}
                      accessibilityLabel={`${m.toString().padStart(2, '0')}${
                        m === minute ? ` (${isArabic ? 'محدد' : 'selected'})` : ''
                      }`}
                      accessibilityRole="button"
                      accessibilityState={{ selected: m === minute }}
                    >
                      <Text
                        style={[
                          styles.timeOptionText,
                          {
                            color: m === minute ? theme.colors.primary : theme.colors.text,
                            fontWeight: m === minute ? '700' : '400',
                          },
                        ]}
                      >
                        {m.toString().padStart(2, '0')}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>

            <TouchableOpacity
              style={[styles.timePickerCloseButton, { backgroundColor: theme.colors.primary }]}
              onPress={() => setVisible(false)}
              accessible={true}
              accessibilityLabel={isArabic ? 'إغلاق منتقي الوقت' : 'Close time picker'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لإغلاق منتقي الوقت'
                  : 'Double tap to close time picker'
              }
              accessibilityRole="button"
            >
              <Text style={styles.timePickerCloseText}>{isArabic ? 'إغلاق' : 'Close'}</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    );
  };

  const handlePermissionDenied = (result: {
    granted: boolean;
    status: string;
    shouldOpenSettings: boolean;
  }) => {
    if (result.shouldOpenSettings) {
      Alert.alert(
        isArabic ? '🔔 الإشعارات مطلوبة' : '🔔 Notifications Required',
        isArabic
          ? 'للحصول على تنبيهات فورية للتنبيهات الحرجة، يرجى تفعيل الإشعارات من إعدادات الجهاز.\n\n⚙️ الإعدادات > IoT Edge > الإشعارات'
          : 'To receive instant alerts for critical alarms, please enable notifications in device settings.\n\n⚙️ Settings > IoT Edge > Notifications',
        [
          {
            text: isArabic ? 'إلغاء' : 'Cancel',
            style: 'cancel',
          },
          {
            text: isArabic ? 'فتح الإعدادات' : 'Open Settings',
            onPress: () => {
              if (Platform.OS === 'ios') {
                Linking.openSettings();
              } else {
                Linking.openSettings();
              }
            },
          },
        ],
      );
    } else {
      Alert.alert(
        isArabic ? 'تم الرفض' : 'Denied',
        isArabic ? 'لم يتم منح أذونات الإشعارات' : 'Notification permissions denied',
      );
    }
  };

  const requestPermissions = async () => {
    // Check if running on simulator/web
    if (!isPhysicalDevice) {
      Alert.alert(
        isArabic ? '📱 جهاز فعلي مطلوب' : '📱 Physical Device Required',
        isArabic
          ? '⚠️ الإشعارات تعمل فقط على الأجهزة الفعلية (iPhone/iPad).\n\n🖥️ المحاكي لا يدعم الإشعارات.'
          : '⚠️ Notifications only work on physical devices (iPhone/iPad).\n\n🖥️ Simulator does not support notifications.',
      );
      return;
    }

    if (Platform.OS === 'web') {
      Alert.alert(
        isArabic ? 'غير متاح' : 'Not Available',
        isArabic
          ? 'الإشعارات غير متوفرة على متصفح الويب.'
          : 'Push notifications are not available on web browsers.',
      );
      return;
    }

    try {
      const result = await notificationService.ensureNotificationsPermission();
      logger.log('📱 Permission request result:', result);

      if (result.granted) {
        setPermissionsGranted(true);

        Alert.alert(
          isArabic ? '✅ تم التفعيل بنجاح!' : '✅ Successfully Enabled!',
          isArabic
            ? '🔔 سوف تصلك إشعارات فورية عند حدوث تنبيهات حرجة'
            : '🔔 You will now receive instant alerts for critical alarms',
        );

        // ❌ REMOVED: Duplicate registration call (already registered during login)
        // This screen is ONLY for managing notification preferences
        // FCM registration happens ONCE at login in AuthContext
        logger.log('✅ Notification settings updated - FCM token already registered');
      } else {
        setPermissionsGranted(false);
        handlePermissionDenied(result);
      }
    } catch (error) {
      logger.error('Failed to request permissions:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'حدث خطأ أثناء طلب الأذونات' : 'An error occurred while requesting permissions',
      );
    }
  };

  const testNotification = async () => {
    // Check if running on simulator/web
    if (!isPhysicalDevice) {
      Alert.alert(
        isArabic ? 'غير متاح' : 'Not Available',
        isArabic
          ? 'الإشعارات تعمل فقط على الأجهزة الفعلية.\n\nالمحاكي لا يدعم الإشعارات المحلية.'
          : 'Notifications only work on physical devices.\n\nSimulator does not support local notifications.',
      );
      return;
    }

    if (Platform.OS === 'web') {
      Alert.alert(
        isArabic ? 'غير متاح' : 'Not Available',
        isArabic
          ? 'الإشعارات غير متوفرة على متصفح الويب.'
          : 'Push notifications are not available on web browsers.',
      );
      return;
    }

    // Ensure permissions before sending test notification
    const permissionResult = await notificationService.ensureNotificationsPermission();
    if (!permissionResult.granted) {
      handlePermissionDenied(permissionResult);
      return;
    }

    try {
      await notificationService.scheduleAlarmNotification(
        isArabic ? 'جهاز اختباري' : 'Test Device',
        'warning',
        isArabic
          ? 'هذا إشعار تجريبي للتأكد من أن الإشعارات تعمل بشكل صحيح.'
          : 'This is a test notification to verify that notifications are working correctly.',
        'test-device-id',
      );

      Alert.alert(
        isArabic ? 'تم الإرسال' : 'Sent',
        isArabic
          ? 'تم إرسال الإشعار التجريبي! يجب أن يظهر خلال ثوانٍ.'
          : 'Test notification sent! It should appear in a few seconds.',
      );

      logger.log('✅ Test notification scheduled successfully');
    } catch (error) {
      logger.error('Failed to send test notification:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل إرسال الإشعار التجريبي' : 'Failed to send test notification',
      );
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {syncWarning && (
        <View style={{ backgroundColor: '#f39c12', paddingVertical: 8, paddingHorizontal: 16 }}>
          <Text style={{ color: '#fff', textAlign: 'center', fontSize: 13 }}>
            {isArabic
              ? 'تم الحفظ محلياً — فشل المزامنة مع الخادم'
              : 'Saved locally — server sync failed'}
          </Text>
        </View>
      )}
      <ScrollView>
        <View style={styles.content}>
          {/* Notification Types */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {isArabic ? 'أنواع الإشعارات' : 'Notification Types'}
            </Text>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="alert-circle" size={24} color={theme.colors.error} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'إشعارات الإنذار' : 'Alarm Notifications'}
                  </Text>
                  <Text
                    style={[
                      styles.settingDescription,
                      { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'تنبيهات عند تجاوز الحدود' : 'Alerts when thresholds are exceeded'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.alarmsEnabled}
                onValueChange={(value) => updatePreference('alarmsEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.alarmsEnabled ? theme.colors.primary : theme.colors.textSecondary
                }
                accessible={true}
                accessibilityLabel={
                  isArabic
                    ? `إشعارات الإنذار: ${preferences.alarmsEnabled ? 'مفعلة' : 'معطلة'}`
                    : `Alarm Notifications: ${preferences.alarmsEnabled ? 'Enabled' : 'Disabled'}`
                }
                accessibilityHint={
                  isArabic
                    ? preferences.alarmsEnabled
                      ? 'انقر نقرًا مزدوجًا لتعطيل إشعارات الإنذار'
                      : 'انقر نقرًا مزدوجًا لتفعيل إشعارات الإنذار'
                    : preferences.alarmsEnabled
                    ? 'Double tap to disable alarm notifications'
                    : 'Double tap to enable alarm notifications'
                }
                accessibilityRole="switch"
              />
            </View>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="battery-dead" size={24} color={theme.colors.warning} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'بطارية منخفضة' : 'Low Battery'}
                  </Text>
                  <Text
                    style={[
                      styles.settingDescription,
                      { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'تنبيه عند انخفاض البطارية' : 'Alert when battery is low'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.lowBatteryEnabled}
                onValueChange={(value) => updatePreference('lowBatteryEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.lowBatteryEnabled ? theme.colors.primary : theme.colors.textSecondary
                }
                accessible={true}
                accessibilityLabel={
                  isArabic
                    ? `بطارية منخفضة: ${preferences.lowBatteryEnabled ? 'مفعلة' : 'معطلة'}`
                    : `Low Battery: ${preferences.lowBatteryEnabled ? 'Enabled' : 'Disabled'}`
                }
                accessibilityHint={
                  isArabic
                    ? preferences.lowBatteryEnabled
                      ? 'انقر نقرًا مزدوجًا لتعطيل تنبيهات البطارية'
                      : 'انقر نقرًا مزدوجًا لتفعيل تنبيهات البطارية'
                    : preferences.lowBatteryEnabled
                    ? 'Double tap to disable low battery alerts'
                    : 'Double tap to enable low battery alerts'
                }
                accessibilityRole="switch"
              />
            </View>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="wifi-outline" size={24} color={theme.colors.textSecondary} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'جهاز غير متصل' : 'Device Offline'}
                  </Text>
                  <Text
                    style={[
                      styles.settingDescription,
                      { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'تنبيه عند انقطاع الاتصال' : 'Alert when device goes offline'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.deviceOfflineEnabled}
                onValueChange={(value) => updatePreference('deviceOfflineEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.deviceOfflineEnabled
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
                accessible={true}
                accessibilityLabel={
                  isArabic
                    ? `جهاز غير متصل: ${preferences.deviceOfflineEnabled ? 'مفعل' : 'معطل'}`
                    : `Device Offline: ${preferences.deviceOfflineEnabled ? 'Enabled' : 'Disabled'}`
                }
                accessibilityHint={
                  isArabic
                    ? preferences.deviceOfflineEnabled
                      ? 'انقر نقرًا مزدوجًا لتعطيل تنبيهات انقطاع الاتصال'
                      : 'انقر نقرًا مزدوجًا لتفعيل تنبيهات انقطاع الاتصال'
                    : preferences.deviceOfflineEnabled
                    ? 'Double tap to disable offline alerts'
                    : 'Double tap to enable offline alerts'
                }
                accessibilityRole="switch"
              />
            </View>
          </View>

          {/* Sound & Vibration */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {isArabic ? 'الصوت والاهتزاز' : 'Sound & Vibration'}
            </Text>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="volume-high" size={24} color={theme.colors.primary} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'الصوت' : 'Sound'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.soundEnabled}
                onValueChange={(value) => updatePreference('soundEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.soundEnabled ? theme.colors.primary : theme.colors.textSecondary
                }
              />
            </View>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="phone-portrait" size={24} color={theme.colors.primary} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'الاهتزاز' : 'Vibration'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.vibrationEnabled}
                onValueChange={(value) => updatePreference('vibrationEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.vibrationEnabled ? theme.colors.primary : theme.colors.textSecondary
                }
              />
            </View>
          </View>

          {/* Quiet Hours */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {isArabic ? 'ساعات الهدوء' : 'Quiet Hours'}
            </Text>
            <Text style={[styles.sectionDescription, { color: theme.colors.textSecondary }]}>
              {isArabic
                ? 'لن يتم إرسال إشعارات خلال ساعات الهدوء'
                : 'No notifications will be sent during quiet hours'}
            </Text>

            <View style={[styles.settingRow, { flexDirection: 'row' }]}>
              <View style={[styles.settingInfo, { flexDirection: 'row' }]}>
                <Ionicons name="moon" size={24} color={theme.colors.primary} />
                <View style={styles.settingText}>
                  <Text
                    style={[
                      styles.settingLabel,
                      { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                    ]}
                  >
                    {isArabic ? 'تفعيل ساعات الهدوء' : 'Enable Quiet Hours'}
                  </Text>
                </View>
              </View>
              <Switch
                value={preferences.quietHoursEnabled}
                onValueChange={(value) => updatePreference('quietHoursEnabled', value)}
                trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                thumbColor={
                  preferences.quietHoursEnabled ? theme.colors.primary : theme.colors.textSecondary
                }
              />
            </View>

            {preferences.quietHoursEnabled && (
              <>
                <TouchableOpacity
                  style={[
                    styles.timeButton,
                    { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                  ]}
                  onPress={() => setShowTimePickerStart(true)}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.timeButtonLabel, { color: theme.colors.textSecondary }]}>
                      {isArabic ? 'وقت البداية' : 'Start Time'}
                    </Text>
                    <Text style={[styles.timeButtonValue, { color: theme.colors.text }]}>
                      {preferences.quietHoursStart}
                    </Text>
                  </View>
                  <Ionicons name="time-outline" size={24} color={theme.colors.primary} />
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.timeButton,
                    { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                  ]}
                  onPress={() => setShowTimePickerEnd(true)}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.timeButtonLabel, { color: theme.colors.textSecondary }]}>
                      {isArabic ? 'وقت النهاية' : 'End Time'}
                    </Text>
                    <Text style={[styles.timeButtonValue, { color: theme.colors.text }]}>
                      {preferences.quietHoursEnd}
                    </Text>
                  </View>
                  <Ionicons name="time-outline" size={24} color={theme.colors.primary} />
                </TouchableOpacity>

                <View
                  style={[
                    styles.infoBox,
                    {
                      backgroundColor: theme.colors.info + '15',
                      borderColor: theme.colors.info,
                      marginTop: 12,
                    },
                  ]}
                >
                  <Ionicons name="information-circle" size={20} color={theme.colors.info} />
                  <Text style={[styles.infoText, { color: theme.colors.text, fontSize: 12 }]}>
                    {isArabic
                      ? `الفترة الحالية: ${preferences.quietHoursStart} - ${preferences.quietHoursEnd}`
                      : `Current period: ${preferences.quietHoursStart} - ${preferences.quietHoursEnd}`}
                  </Text>
                </View>
              </>
            )}
          </View>

          {/* Actions */}
          <View style={styles.actionsContainer}>
            {!permissionsGranted && (
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#f59e0b', flexDirection: 'row' }]}
                onPress={requestPermissions}
              >
                <Ionicons name="notifications" size={20} color="#FFF" />
                <Text style={styles.actionButtonText}>
                  {isArabic ? '🔔 طلب أذونات الإشعارات' : '🔔 Request Permissions'}
                </Text>
              </TouchableOpacity>
            )}

            {permissionsGranted && (
              <TouchableOpacity
                style={[
                  styles.actionButton,
                  { backgroundColor: theme.colors.success, flexDirection: 'row' },
                ]}
                onPress={testNotification}
              >
                <Ionicons name="flask" size={20} color="#FFF" />
                <Text style={styles.actionButtonText}>
                  {isArabic ? '🧪 اختبار إشعار' : '🧪 Test Notification'}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[
                styles.actionButton,
                { backgroundColor: theme.colors.error, flexDirection: 'row' },
              ]}
              onPress={async () => {
                Alert.alert(
                  isArabic ? 'إعادة تعيين الإعدادات' : 'Reset Settings',
                  isArabic
                    ? 'هل تريد إعادة جميع الإعدادات إلى القيم الافتراضية؟'
                    : 'Do you want to reset all settings to default values?',
                  [
                    {
                      text: isArabic ? 'إلغاء' : 'Cancel',
                      style: 'cancel',
                    },
                    {
                      text: isArabic ? 'إعادة تعيين' : 'Reset',
                      style: 'destructive',
                      onPress: async () => {
                        const defaultPrefs: NotificationPreferences = {
                          alarmsEnabled: true,
                          lowBatteryEnabled: true,
                          deviceOfflineEnabled: true,
                          soundEnabled: true,
                          vibrationEnabled: true,
                          quietHoursEnabled: false,
                          quietHoursStart: '22:00',
                          quietHoursEnd: '07:00',
                        };
                        setPreferences(defaultPrefs);
                        await notificationService.savePreferences(defaultPrefs);
                        Alert.alert(
                          isArabic ? 'تم' : 'Done',
                          isArabic
                            ? 'تم إعادة تعيين الإعدادات بنجاح'
                            : 'Settings reset successfully',
                        );
                      },
                    },
                  ],
                );
              }}
            >
              <Ionicons name="refresh" size={20} color="#FFF" />
              <Text style={styles.actionButtonText}>
                {isArabic ? '🔄 إعادة تعيين الإعدادات' : '🔄 Reset Settings'}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Settings Summary */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {isArabic ? 'ملخص الإعدادات' : 'Settings Summary'}
            </Text>

            <View style={styles.summaryGrid}>
              <View
                style={[
                  styles.summaryCard,
                  {
                    backgroundColor: preferences.alarmsEnabled
                      ? theme.colors.success + '15'
                      : theme.colors.border + '30',
                  },
                ]}
              >
                <Ionicons
                  name="alert-circle"
                  size={24}
                  color={
                    preferences.alarmsEnabled ? theme.colors.success : theme.colors.textSecondary
                  }
                />
                <Text style={[styles.summaryCardTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'الإنذارات' : 'Alarms'}
                </Text>
                <Text
                  style={[
                    styles.summaryCardValue,
                    {
                      color: preferences.alarmsEnabled
                        ? theme.colors.success
                        : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {preferences.alarmsEnabled
                    ? isArabic
                      ? 'مفعل'
                      : 'ON'
                    : isArabic
                    ? 'معطل'
                    : 'OFF'}
                </Text>
              </View>

              <View
                style={[
                  styles.summaryCard,
                  {
                    backgroundColor: preferences.lowBatteryEnabled
                      ? theme.colors.warning + '15'
                      : theme.colors.border + '30',
                  },
                ]}
              >
                <Ionicons
                  name="battery-dead"
                  size={24}
                  color={
                    preferences.lowBatteryEnabled
                      ? theme.colors.warning
                      : theme.colors.textSecondary
                  }
                />
                <Text style={[styles.summaryCardTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'البطارية' : 'Battery'}
                </Text>
                <Text
                  style={[
                    styles.summaryCardValue,
                    {
                      color: preferences.lowBatteryEnabled
                        ? theme.colors.warning
                        : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {preferences.lowBatteryEnabled
                    ? isArabic
                      ? 'مفعل'
                      : 'ON'
                    : isArabic
                    ? 'معطل'
                    : 'OFF'}
                </Text>
              </View>

              <View
                style={[
                  styles.summaryCard,
                  {
                    backgroundColor: preferences.deviceOfflineEnabled
                      ? theme.colors.info + '15'
                      : theme.colors.border + '30',
                  },
                ]}
              >
                <Ionicons
                  name="wifi-outline"
                  size={24}
                  color={
                    preferences.deviceOfflineEnabled
                      ? theme.colors.info
                      : theme.colors.textSecondary
                  }
                />
                <Text style={[styles.summaryCardTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'الاتصال' : 'Offline'}
                </Text>
                <Text
                  style={[
                    styles.summaryCardValue,
                    {
                      color: preferences.deviceOfflineEnabled
                        ? theme.colors.info
                        : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {preferences.deviceOfflineEnabled
                    ? isArabic
                      ? 'مفعل'
                      : 'ON'
                    : isArabic
                    ? 'معطل'
                    : 'OFF'}
                </Text>
              </View>

              <View
                style={[
                  styles.summaryCard,
                  {
                    backgroundColor: preferences.soundEnabled
                      ? theme.colors.primary + '15'
                      : theme.colors.border + '30',
                  },
                ]}
              >
                <Ionicons
                  name="volume-high"
                  size={24}
                  color={
                    preferences.soundEnabled ? theme.colors.primary : theme.colors.textSecondary
                  }
                />
                <Text style={[styles.summaryCardTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'الصوت' : 'Sound'}
                </Text>
                <Text
                  style={[
                    styles.summaryCardValue,
                    {
                      color: preferences.soundEnabled
                        ? theme.colors.primary
                        : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {preferences.soundEnabled
                    ? isArabic
                      ? 'مفعل'
                      : 'ON'
                    : isArabic
                    ? 'معطل'
                    : 'OFF'}
                </Text>
              </View>
            </View>
          </View>

          {/* Info Box */}
          <View
            style={[
              styles.infoBox,
              {
                backgroundColor: permissionsGranted
                  ? theme.colors.success + '15'
                  : theme.colors.warning + '15',
                borderColor: permissionsGranted ? theme.colors.success : theme.colors.warning,
                flexDirection: 'row',
              },
            ]}
          >
            <Ionicons
              name={permissionsGranted ? 'checkmark-circle' : 'alert-circle'}
              size={24}
              color={permissionsGranted ? theme.colors.success : theme.colors.warning}
            />
            <Text
              style={[
                styles.infoText,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {permissionsGranted
                ? isArabic
                  ? '✅ الإشعارات مفعلة! سوف تصلك تنبيهات فورية.'
                  : '✅ Notifications enabled! You will receive instant alerts.'
                : isArabic
                ? '⚠️ الإشعارات معطلة. اضغط "طلب أذونات الإشعارات" للتفعيل.'
                : '⚠️ Notifications disabled. Tap "Request Permissions" to enable.'}
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Time Pickers */}
      {renderTimePicker('start')}
      {renderTimePicker('end')}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  section: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  settingText: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '500',
  },
  settingDescription: {
    fontSize: 12,
    marginTop: 2,
  },
  actionsContainer: {
    gap: 12,
    marginBottom: 16,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  actionButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
  infoBox: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  timeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 12,
  },
  timeButtonLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  timeButtonValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  timePickerContainer: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingBottom: 32,
    maxHeight: '70%',
  },
  timePickerHeader: {
    padding: 20,
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  timePickerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  timePickerContent: {
    maxHeight: 300,
  },
  timePicker: {
    maxHeight: 300,
  },
  timeOption: {
    padding: 16,
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 4,
  },
  timeOptionText: {
    fontSize: 16,
    textAlign: 'center',
  },
  timePickerColumnLabel: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
    paddingVertical: 8,
  },
  timePickerSeparator: {
    fontSize: 24,
    fontWeight: '700',
    alignSelf: 'center',
    marginHorizontal: 4,
    marginTop: 32,
  },
  timePickerCloseButton: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  timePickerCloseText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  summaryCard: {
    flex: 1,
    minWidth: '45%',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    gap: 8,
  },
  summaryCardTitle: {
    fontSize: 12,
    fontWeight: '500',
  },
  summaryCardValue: {
    fontSize: 14,
    fontWeight: '700',
  },
});
